import {
  List,
  ListItemButton,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  Chip,
  Stack,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  OpportunityInterestStatus,
  PsiOpportunityInterestDTO,
  OpportunityInterestRejectReason,
} from '@inversor-hub/types';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EuroIcon from '@mui/icons-material/Euro';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { formatPercentage } from '@/utils/misc.ts';
import { psiServiceLabels } from '@/features/psi/constants.ts';

interface InvestorListProps {
  interests?: PsiOpportunityInterestDTO[];
  isLoading: boolean;
  selectedInterestId: string | null;
  onSelectInterest: (interestId: string) => void;
}

const getInterestStatusChip = (
  status: OpportunityInterestStatus,
  rejectReason?: OpportunityInterestRejectReason
) => {
  switch (status) {
    case 'REJECTED':
      if (rejectReason === 'STALE') {
        return (
          <Chip
            size="small"
            icon={<CancelIcon fontSize="small" />}
            label="Tiempo de reserva expirado"
            color="error"
            variant="outlined"
            sx={{ fontWeight: 'medium' }}
          />
        );
      }
      if (rejectReason === 'INVESTOR_DECLINED') {
        return (
          <Chip
            size="small"
            icon={<PersonOffIcon fontSize="small" />}
            label="Rechazado por el inversor"
            color="error"
            variant="outlined"
            sx={{ fontWeight: 'medium' }}
          />
        );
      }
      // Default rejected chip if no specific reason
      return (
        <Chip
          size="small"
          icon={<CancelIcon fontSize="small" />}
          label="Rechazado"
          color="error"
          variant="outlined"
          sx={{ fontWeight: 'medium' }}
        />
      );

    case 'REMOVED':
      return (
        <Chip
          size="small"
          icon={<PersonOffIcon fontSize="small" />}
          label="Ya no está interesado"
          color="error"
          variant="outlined"
          sx={{ fontWeight: 'medium' }}
        />
      );

    case 'PENDING':
    case 'SELECTED':
    case 'RESERVED':
      return null;

    default:
      return null;
  }
};

const getInvestmentTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    TRADITIONAL_RENT: 'Alquiler Tradicional',
    ROOM_RENT: 'Alquiler por Habitaciones',
    FLIPPING: 'Flipping',
    VACATIONAL: 'Vacacional',
    CHANGE_OF_USE: 'Cambio de Uso',
  };
  return labels[type] || type;
};

const formatCurrency = (cents: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(cents / 100);
};

export const InvestorList = ({
  interests,
  isLoading,
  selectedInterestId,
  onSelectInterest,
}: InvestorListProps) => {
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!interests || interests.length === 0) {
    return (
      <Typography variant="body1" color="text.secondary" align="center" py={2}>
        No hay inversores interesados en esta oportunidad
      </Typography>
    );
  }

  // Sort interests to show selectable ones first
  const sortedInterests = [...interests].sort((a, b) => {
    const aCanBeSelected = a.status === 'PENDING';
    const bCanBeSelected = b.status === 'PENDING';

    if (aCanBeSelected && !bCanBeSelected) return -1;
    if (!aCanBeSelected && bCanBeSelected) return 1;

    // sort by created at desc
    return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
  });

  return (
    <List
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 0,
      }}
    >
      {sortedInterests.map((interest, index) => {
        const canBeSelected = interest.status === 'PENDING';
        const isSelected = selectedInterestId === interest.id;
        const prefs = interest.investor.preferences;

        return (
          <Box key={interest.id}>
            {index > 0 && <Divider />}
            <ListItemButton
              selected={isSelected}
              onClick={() => canBeSelected && onSelectInterest(interest.id)}
              disabled={!canBeSelected}
              sx={{
                py: 1.5,
                px: 2,
                opacity: !canBeSelected ? 0.6 : 1,
                transition: 'all 0.2s ease-in-out',
                '&.Mui-selected': {
                  bgcolor: 'primary.lighter',
                  borderLeft: 3,
                  borderColor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.light',
                  },
                },
                '&:hover': {
                  bgcolor: canBeSelected ? 'action.hover' : 'transparent',
                },
                '&.Mui-disabled': {
                  opacity: 0.6,
                },
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="flex-start" width="100%">
                {/* Avatar with selection indicator */}
                <Box position="relative" flexShrink={0}>
                  <Avatar
                    src={interest.investor.photoUrl}
                    alt={interest.investor.name}
                    sx={{
                      width: 56,
                      height: 56,
                      filter: !canBeSelected ? 'grayscale(100%)' : 'none',
                      border: isSelected ? 2 : 0,
                      borderColor: 'primary.main',
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {interest.investor.name.charAt(0).toUpperCase()}
                  </Avatar>
                  {isSelected && (
                    <CheckCircleIcon
                      sx={{
                        position: 'absolute',
                        bottom: -4,
                        right: -4,
                        bgcolor: 'background.paper',
                        borderRadius: '50%',
                        color: 'primary.main',
                        fontSize: 20,
                      }}
                    />
                  )}
                </Box>

                {/* Investor info */}
                <Stack spacing={0.75} flex={1} minWidth={0}>
                  {/* Name and status */}
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      sx={{
                        textDecoration: !canBeSelected ? 'line-through' : 'none',
                        color: !canBeSelected ? 'text.disabled' : 'text.primary',
                      }}
                    >
                      {interest.investor.name}
                    </Typography>
                    {getInterestStatusChip(interest.status, interest.rejectReason)}
                  </Stack>

                  {/* Contact info - compact layout */}
                  {canBeSelected ? (
                    <Stack spacing={0.5}>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {interest.investor.email}
                          </Typography>
                        </Stack>
                        {interest.investor.phone && (
                          <Stack direction="row" spacing={0.75} alignItems="center">
                            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary">
                              {interest.investor.phone}
                            </Typography>
                          </Stack>
                        )}
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <AccessTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            Interesado desde{' '}
                            {new Date(interest.createdAt).toLocaleDateString('es-ES', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}{' '}
                            a las{' '}
                            {new Date(interest.createdAt).toLocaleTimeString('es-ES', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                        </Stack>

                        {/* Selected Services */}
                        {interest.selectedServices && interest.selectedServices.length > 0 && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            alignItems="center"
                            flexWrap="wrap"
                            gap={0.5}
                          >
                            <HandshakeIcon sx={{ fontSize: 16, color: 'info.main' }} />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={600}
                              sx={{ mr: 0.5 }}
                            >
                              Servicios requeridos:
                            </Typography>
                            {interest.selectedServices.map((service) => (
                              <Chip
                                key={service}
                                label={psiServiceLabels[service]}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: 'info.lighter',
                                  color: 'info.dark',
                                  fontWeight: 500,
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                      </Stack>

                      {/* Investor Preferences Section */}
                      {(prefs.desiredInvestmentTypes?.length > 0 ||
                        prefs.preferredProvinces?.length > 0 ||
                        (prefs.averageTicket && prefs.averageTicket > 0) ||
                        (prefs.expectedGrossReturn && prefs.expectedGrossReturn > 0) ||
                        prefs.requiresMortgage) && (
                        <Box
                          sx={{
                            mt: 0.5,
                            pt: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Stack spacing={0.75}>
                            {/* Investment Types */}
                            {prefs.desiredInvestmentTypes &&
                              prefs.desiredInvestmentTypes.length > 0 && (
                                <Stack
                                  direction="row"
                                  spacing={0.5}
                                  alignItems="center"
                                  flexWrap="wrap"
                                  gap={0.5}
                                >
                                  <HomeWorkIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={600}
                                    sx={{ mr: 0.5 }}
                                  >
                                    Tipos:
                                  </Typography>
                                  {prefs.desiredInvestmentTypes.map((type) => (
                                    <Chip
                                      key={type}
                                      label={getInvestmentTypeLabel(type)}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        bgcolor: 'primary.lighter',
                                        color: 'primary.main',
                                        fontWeight: 500,
                                      }}
                                    />
                                  ))}
                                </Stack>
                              )}

                            {/* Provinces and Financial Info */}
                            <Stack
                              direction="row"
                              spacing={1.5}
                              alignItems="center"
                              flexWrap="wrap"
                              gap={0.5}
                            >
                              {/* Provinces */}
                              {prefs.preferredProvinces && prefs.preferredProvinces.length > 0 && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <LocationOnIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  <Tooltip
                                    title={prefs.preferredProvinces.join(', ')}
                                    arrow
                                    placement="top"
                                  >
                                    <Typography variant="caption" color="text.secondary">
                                      {prefs.preferredProvinces.length === 1
                                        ? prefs.preferredProvinces[0]
                                        : `${prefs.preferredProvinces.length} provincias`}
                                    </Typography>
                                  </Tooltip>
                                </Stack>
                              )}

                              {/* Average Ticket */}
                              {prefs.averageTicket && prefs.averageTicket > 0 && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <EuroIcon sx={{ fontSize: 16, color: 'info.main' }} />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    {formatCurrency(prefs.averageTicket)} ticket medio
                                  </Typography>
                                </Stack>
                              )}

                              {/* Expected Return */}
                              {prefs.expectedGrossReturn && prefs.expectedGrossReturn > 0 && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={500}
                                  >
                                    {formatPercentage(prefs.expectedGrossReturn)} rentabilidad
                                  </Typography>
                                </Stack>
                              )}

                              {/* Mortgage Requirement */}
                              {prefs.requiresMortgage && (
                                <Chip
                                  icon={<AccountBalanceIcon sx={{ fontSize: 14 }} />}
                                  label="Requiere hipoteca"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: 'warning.lighter',
                                    color: 'warning.dark',
                                    '& .MuiChip-icon': {
                                      color: 'warning.main',
                                    },
                                  }}
                                />
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                      Este inversor ya no puede ser seleccionado
                    </Typography>
                  )}
                </Stack>
              </Stack>
            </ListItemButton>
          </Box>
        );
      })}
    </List>
  );
};
